import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  BarChart3, Loader2, DollarSign, Users, Percent, CalendarRange, Download, FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);
const fmtCompact = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v || 0);

function StatCard({ label, value, icon: Icon, accent = 'blue' }) {
  const accentClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accentClasses[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function CabexReportsPage() {
  const { currentCompany } = useCompany();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/cabex/reports`, {
        params: { company_id: currentCompany.id },
        withCredentials: true,
      });
      setReport(res.data);
    } catch {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const exportCSV = () => {
    if (!report?.per_client?.length) {
      toast.error('No data to export');
      return;
    }
    const headers = [
      'Client Name', 'Cabex ID', 'Contract Date', 'Payment Day',
      'Contract Amount', 'Contract Fee', 'Client %', 'Monthly Payout', 'Annual Payout',
    ];
    const rows = report.per_client.map((c) => [
      c.client_name || '',
      c.cabex_id || '',
      c.contract_date || '',
      c.payment_day != null ? String(c.payment_day) : '',
      c.contract_amount ?? '',
      c.contract_fee ?? '',
      c.client_percentage ?? '',
      c.monthly_payout ?? '',
      c.annual_payout ?? '',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cabex-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const printReport = () => {
    window.print();
  };

  if (!currentCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a company</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!report || report.total_clients === 0) {
    return (
      <div className="space-y-6" data-testid="cabex-reports-page">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Cabex Reports</h1>
          <p className="text-gray-500 mt-1">Contract and payout analytics</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No data to report yet</h3>
          <p className="text-gray-500">Add a Cabex client to start generating reports.</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const topClients = report.per_client.slice(0, 10);
  const paymentDayData = [
    { name: '1st of month', value: report.payment_day_split[1] || 0 },
    { name: '15th of month', value: report.payment_day_split[15] || 0 },
    ...((report.payment_day_split.other || 0) > 0
      ? [{ name: 'Unspecified', value: report.payment_day_split.other }]
      : []),
  ];

  const COLORS = ['#0EA5E9', '#22C55E', '#A1A1AA'];

  return (
    <div className="space-y-6" data-testid="cabex-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Cabex Reports</h1>
          <p className="text-gray-500 mt-1">Contract and payout analytics</p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" onClick={printReport} data-testid="print-report-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Print / PDF
          </Button>
          <Button onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Clients" value={report.total_clients} icon={Users} accent="blue" />
        <StatCard label="Total Contract Amount" value={fmtCompact(report.total_contract_amount)} icon={DollarSign} accent="green" />
        <StatCard label="Total Contract Fees" value={fmtCompact(report.total_contract_fee)} icon={Percent} accent="purple" />
        <StatCard label="Annual Payout to Clients" value={fmtCompact(report.annual_payout_total)} icon={CalendarRange} accent="amber" />
      </div>

      {/* Monthly payout total */}
      <div className="bg-gradient-to-r from-blue-600 to-sky-500 text-white rounded-xl p-6 shadow-sm">
        <p className="text-sm font-medium text-blue-100 uppercase tracking-wide">Combined Monthly Payout to All Clients</p>
        <p className="text-4xl font-semibold mt-2">{fmtCurrency(report.monthly_payout_total)}</p>
        <p className="text-sm text-blue-100 mt-1">
          {report.payment_day_split[1] || 0} on the 1st · {report.payment_day_split[15] || 0} on the 15th
          {(report.payment_day_split.other || 0) > 0 ? ` · ${report.payment_day_split.other} unscheduled` : ''}
        </p>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-medium text-gray-900">Top {Math.min(10, topClients.length)} Clients by Contract Amount</h2>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={topClients} margin={{ top: 5, right: 16, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="client_name"
                  tick={{ fontSize: 11 }}
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(v)} />
                <Tooltip formatter={(v) => fmtCurrency(v)} />
                <Bar dataKey="contract_amount" name="Contract Amount" radius={[4, 4, 0, 0]}>
                  {topClients.map((_, i) => (
                    <Cell key={i} fill="#0EA5E9" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-medium text-gray-900">Payment Day Split</h2>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={paymentDayData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  dataKey="value"
                  label={(d) => `${d.value}`}
                >
                  {paymentDayData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} clients`, n]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Per-client table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-medium text-gray-900">Per-Client Breakdown</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cabex ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Day</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Contract</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fee</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Client %</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monthly Payout</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Annual Payout</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {report.per_client.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{c.client_name}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{c.cabex_id || '-'}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{c.contract_date || '-'}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{c.payment_day || '-'}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-900 font-medium">{fmtCurrency(c.contract_amount)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700">{fmtCurrency(c.contract_fee)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-700">{c.client_percentage != null ? `${c.client_percentage}%` : '-'}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-blue-700 font-medium">{fmtCurrency(c.monthly_payout)}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-emerald-700 font-medium">{fmtCurrency(c.annual_payout)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-900" colSpan={4}>Totals</td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{fmtCurrency(report.total_contract_amount)}</td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{fmtCurrency(report.total_contract_fee)}</td>
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-blue-700">{fmtCurrency(report.monthly_payout_total)}</td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-emerald-700">{fmtCurrency(report.annual_payout_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
